import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CommentsResponse } from 'src/app/models/comment';
import { ExportFilter } from 'src/app/models/export-filter';
import { FormDataHistoryResponse } from 'src/app/models/form-data-history';
import { StatisticsResponse } from 'src/app/models/statistics';
import { JSONFormData, JSONFormDataResponse } from '../../models/form-data';
import { Pagination } from '../../models/pagination';
import { BaseService } from '../base.service';
import { StringHelper } from '../helpers/string.helper';

@Injectable({
  providedIn: 'root'
})
export class FormDataService extends BaseService {
  private formsDataUrl = this.apiUrl + '/formData';

  getFormsData(type = '', pagination?: Pagination): Observable<JSONFormDataResponse> {
    let params = new HttpParams({});

    if (type) {
      params = new HttpParams({
        fromObject: {
          type: type
        }
      });
    }

    if (pagination) {
      params = new HttpParams({
        fromObject: {
          rows: pagination.per_page.toString(),
          page: pagination.current_page.toString()
        }
      });
    }

    return this.http.get<JSONFormDataResponse>(this.formsDataUrl, { params: params })
      .pipe(
        tap(_ => this.log(`fetched forms data`)),
        tap(data => data.formsData.map(item => item.currentState = StringHelper.trim(item.currentState, '"'))),
        catchError(this.handleError<JSONFormDataResponse>('getFormsData', <JSONFormDataResponse>{}))
      );
  }

  /** GET FormData by id. Will 404 if id not found */
  getFormData(id: number): Observable<JSONFormDataResponse> {
    if (+id === 0) {
      return observableOf(this.initializeFormData());
    }

    const url = `${this.formsDataUrl}/${id}`;
    return this.http.get<JSONFormDataResponse>(url).pipe(
      tap(_ => this.log(`fetched FormData id=${id}`)),
      catchError(this.handleError<JSONFormDataResponse>(`getFormData id=${id}`))
    );
  }


  saveFormData(formData: JSONFormData): Observable<JSONFormDataResponse> {
    // const data = Object.assign(new JSONFormData, formData);
    if (formData.id === 0) {
      return this.addFormData(formData);
    }

    return this.updateFormData(formData);
  }

  /** POST: add a new FormData to the server */
  addFormData(formData: JSONFormData): Observable<JSONFormDataResponse> {
    return this.http.post<JSONFormDataResponse>(this.formsDataUrl, formData, this.httpOptions).pipe(
      tap((newFormData: JSONFormDataResponse) => this.log(`added FormData w/ id=${newFormData.formData.id}`)),
      catchError(this.handleError<JSONFormDataResponse>('addFormData', <JSONFormDataResponse>{}))
    );
  }

  /** PUT: update the FormData on the server */
  updateFormData(formData: JSONFormData): Observable<JSONFormDataResponse> {
    if (formData.id === 0) {
      return observableOf(this.initializeFormData());
    }

    const url = `${this.formsDataUrl}/${formData.id}`;

    return this.http.put<JSONFormDataResponse>(url, formData, this.httpOptions).pipe(
      tap(_ => this.log(`updated FormData id=${formData.id}`)),
      catchError(this.handleError<JSONFormDataResponse>('updateFormData', <JSONFormDataResponse>{}, 'FormData nicht gespeichert!'))
    );
  }

  /** DELETE: delete FormData from the server */
  deleteFormData(formData: JSONFormData | number): Observable<JSONFormDataResponse> {
    const id = typeof formData === 'number' ? formData : formData.id;
    const url = `${this.formsDataUrl}/${id}`;

    return this.http.delete<JSONFormDataResponse>(url, this.httpOptions).pipe(
      tap(_ => this.log(`deleted FormData id=${id}`)),
      catchError(this.handleError<JSONFormDataResponse>('deleteFormData'))
    );
  }

  workflow(task: string, formData: JSONFormData): Observable<JSONFormDataResponse> {
    formData.newState = task;
    return this.http.post<JSONFormDataResponse>(`${this.formsDataUrl}/workflow`, formData, this.httpOptions).pipe(
      tap(_ => this.log(`updated FormData id=${formData.id}`)),
      tap((data) => data.formData.currentState = StringHelper.trim(data.formData.currentState, '"')),
      catchError(this.handleError<JSONFormDataResponse>('updateFormData', <JSONFormDataResponse>{}, 'FormData nicht gespeichert!'))
    );
  }

  comments(id: number): Observable<CommentsResponse> {
    return this.http.post<CommentsResponse>(`${this.formsDataUrl}/comments`, { id: id }).pipe(
      tap(_ => this.log(`comments received: `)),
      catchError(this.handleError<CommentsResponse>('comments', <CommentsResponse>{}, 'Kommentare konnten nicht geladen werden!'))
    );
  }

  history(id: number): Observable<FormDataHistoryResponse> {
    return this.http.post<FormDataHistoryResponse>(`${this.formsDataUrl}/history`, { id: id }).pipe(
      tap(_ => this.log(`history received: `)),
      tap(data => data.history.map(item => {
        const oldState = item.old_values.currentState || null;
        if (oldState) { item.old_values.currentState = StringHelper.trim(oldState, '"'); }
        const newState = item.new_values.currentState || null;
        if (newState) {
          item.new_values.currentState = StringHelper.trim(newState, '"');
        }
      })),
      catchError(this.handleError<FormDataHistoryResponse>('history', <FormDataHistoryResponse>{}, 'History konnten nicht geladen werden!'))
    );
  }

  myComments(type: string): Observable<CommentsResponse> {
    return this.http.post<CommentsResponse>(`${this.formsDataUrl}/myComments`, { type: type }).pipe(
      tap(_ => this.log(`comments received: `)),
      catchError(this.handleError<CommentsResponse>('comments', <CommentsResponse>{}, 'Kommentare konnten nicht geladen werden!'))
    );
  }
  userStatistics(type: string): Observable<StatisticsResponse> {
    return this.http.post<StatisticsResponse>(`${this.formsDataUrl}/userStatistics`, { type: type }).pipe(
      tap(_ => this.log(`statistics received: `)),
      catchError(this.handleError<StatisticsResponse>('statistics', <StatisticsResponse>{}, 'Statistik konnten nicht geladen werden!'))
    );
  }

  states() {
    return this.http.post(`${this.formsDataUrl}/states`, {}).pipe(
      tap(_ => this.log(`transitions received: `)),
      catchError(this.handleError<{}>('states', {}, 'States konnten nicht geladen werden!'))
    );
  }

  export(filter: ExportFilter): Observable<any> {
    return this.http.post<JSONFormDataResponse>(`${this.formsDataUrl}/export`, { filter: filter }, this.httpOptions).pipe(
      tap(),
      catchError(this.handleError<JSONFormDataResponse>('Export', <any>{}, 'Datei konnte nicht erstellt werden!'))
    );
  }
  phoenixExport(courses: number[] = []): Observable<any> {
    return this.http.post<JSONFormDataResponse>(`${this.formsDataUrl}/export/phoenix`, { courses }, this.httpOptions).pipe(
      tap(),
      catchError(this.handleError<JSONFormDataResponse>('Export', <any>{}, 'Phoenix Export hat nicht funktioniert!'))
    );
  }

  downloadFile(route: string): void {
    const headers = new HttpHeaders();
    this.http.get(route, { headers, responseType: 'blob' as 'json' }).subscribe(
      (response: any) => {
        const dataType = response.type;
        const binaryData = [];
        binaryData.push(response);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        // if (filename) { downloadLink.setAttribute('download', filename); }
        document.body.appendChild(downloadLink);
        downloadLink.click();
      }
    );
  }

  /**
   *  Return an initialized object
   */
  initializeFormData(): JSONFormDataResponse {
    return new JSONFormDataResponse();
  }
}
